(ns manage.adset-decision-tree-page.editor.node
  (:require ["@mui/material/Badge$default" :as Badge]
            ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Divider$default" :as Divider]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/InputAdornment$default" :as InputAdornment]
            ["@mui/material/List$default" :as List]
            ["@mui/material/ListItem$default" :as ListItem]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Switch$default" :as Switch]
            ["@mui/material/TextField$default" :as TextField]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/material/styles" :as styles]
            ["@xyflow/react"
             :refer [Handle Position useConnection useUpdateNodeInternals]]
            ["src/svg/wpp/content/calendar.svg$default" :as Calendar]
            [cljs-bean.core :refer [->clj]]
            [clojure.string :as str]
            [common.widgets.menu :refer [menu]]
            [manage.adset-decision-tree-page.editor.util :refer [get-outcome-descendants]]
            [manage.adset-decision-tree-page.event :as event]
            [manage.adset-decision-tree-page.node-library :as node-library]
            [manage.adset-decision-tree-page.rotation :as rotation]
            [manage.adset-decision-tree-page.sub :as sub]
            [manage.adset-decision-tree-page.validation :as validation]
            [manage.adset-decision-tree-page.variants-rotation-ocp.view :as variants-rotation-ocp]
            [manage.adset-decision-tree-page.view.common
             :refer [decision-tree-node-container node-header outcome-label warning-icon]]
            [manage.adset-decision-tree-page.view.context :as context]
            [manage.adset-decision-tree-page.view.hook :as hook]
            [manage.adset-decision-tree-page.view.node-conversion
             :as node-conversion]
            [manage.adset-decision-tree-page.view.side-panel.poi.validation :as poi-validation]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui details-popover [{:keys [items]}]
  ($ Stack {:on-click (fn [^js e] (.stopPropagation e))}
     ($ menu {:icon "more_horiz"
              :items items
              :toggle-props {:sx #js {:p 0.5}}})))

(defui show-path-button [{:keys [edge-id left-distance]}]
  ($ IconButton {:on-click (fn [e]
                             (.stopPropagation e)
                             (rf/dispatch [::event/update-sub-path-visibility
                                           {:edge-id edge-id
                                            :is-visible true}]))
                 :sx #js {:p 0.5
                          ;; Button is placed absolute, moved to the right,
                          ;; outside of the decision node
                          :position "absolute"
                          :transform "translate(100%, -50%)"
                          :marginRight (- (or left-distance 0))
                          :right 0
                          :top "50%"}}
     ($ Icon {:font-size "medium"}
        "add_circle_outline")))

(defui ^:memo source-handle [{:keys [id is-root parent-id]}]
  (let [read-only (use-subscribe [::sub/editor-read-only])
        connect-blocked? (use-subscribe [::sub/rotation-connect-blocked? parent-id])
        [{connected-node-id :id}] (use-subscribe [::sub/children-by-node-id id])
        primary-color (.. (styles/useTheme) -palette -primary -main)
        edge-id (when (and id connected-node-id)
                  (node-conversion/->edge id connected-node-id))
        connected-node-id-is-hidden (use-subscribe
                                     [::sub/reactflow-node-is-hidden connected-node-id])]
    ($ :<>
       (when connected-node-id-is-hidden
         ($ show-path-button {:edge-id edge-id
                              :left-distance (when-not is-root 2)}))
       ($ Box {:sx #js {"& .react-flow__handle.source"
                        #js {"&" #js {:width "28px"
                                      :height "28px"
                                      :backgroundClip "content-box"
                                      :backgroundColor "primary.main"
                                      :borderStyle "solid"
                                      :borderWidth "9px"
                                      :borderColor "rgba(0,0,0,0)"}
                             "&.connected" #js {:backgroundColor "grey.40"}
                             "&:hover" #js {:borderColor (styles/alpha primary-color 0.1)}}}}
          ($ Handle {:class (when (some? connected-node-id) "connected")
                     :type "source"
                     :position (.-Right Position)
                     :id (str id)
                     :isConnectable (and (not read-only) (not connect-blocked?))
                     ;; The implementation is made so that any extra prop will be passed down to the :div being used for rendering the handle.
                     ;; See https://github.com/xyflow/xyflow/blob/%40xyflow/react%4012.4.3/packages/react/src/components/Handle/index.tsx#L249
                     :style (when (or connected-node-id-is-hidden read-only connect-blocked?)
                              #js {:visibility "hidden"})})))))

(defui ^:memo target-handle-view [{:keys [read-only target-active]}]
  ($ Handle {:type "target"
             :position (.-Left Position)
             :isConnectable (not read-only)
             :style #js {:visibility (when-not target-active "hidden")
                         :width 0
                         :height 0
                         :minWidth 0
                         :minHeight 0}}
     ($ Box {:sx #js {:transform "translate(-50%, -50%)"
                      :backgroundColor "background.paper"
                      :borderColor "primary.main"
                      :width "26px"
                      :height "26px"
                      :borderStyle "solid"
                      :padding "12px"
                      :borderRadius "50%"
                      :borderWidth 2}})))

(defui target-handle [{:keys [id parent-id]}]
  (let [read-only (use-subscribe [::sub/editor-read-only])
        connection (->clj (useConnection))]
    ($ target-handle-view
       {:read-only read-only
        :target-active (and (not= (-> connection :fromNode :id) (str id))
                            (or (-> connection :inProgress) (nil? parent-id)))})))

(defhook use-item-sx [{:keys [highlighted selected has-errors selectable]}]
  (let [outline (or has-errors selected)]
    #js {:borderRadius 1
         :px 1
         :py 0.5
         :backgroundColor (cond
                            has-errors "error.light"
                            (or selected highlighted) "primary.selected"
                            :else "grey.80")
         :color (when selected "primary.main")
         :outlineWidth (when outline 2)
         :outlineStyle (when outline "solid")
         :outlineColor (cond
                         has-errors "error.main"
                         selected "primary.main")
         :position "relative"
         :cursor (when selectable "pointer")
         :height 44}))

(defui outcome-list-item
  [{:keys [source-handle-id parent-id on-click label suffix variants children] :as props}]
  ($ ListItem {:on-click (when on-click (fn [e] (.stopPropagation e) (on-click)))
               :direction "row"
               :alignItems "center"
               :sx (use-item-sx props)}
     ($ outcome-label {:label label})
     (when (some (comp :timezone :scheduling) variants)
       ($ SvgIcon {:component Calendar
                   :inheritViewBox true
                   :sx #js {:mr 1}}))
     (when (seq variants)
       ($ Badge {:color "primary"
                 :variant "dot"
                 :overlap "circular"
                 :invisible (not (sub/some-ready-for-update? variants))}
          ($ Chip {:sx #js {:mr 0.5
                            :borderRadius 8}
                   :label (count variants)})))
     (when suffix
       ($ Typography {:color "text.secondary"
                      :sx #js {:mr 1}}
          suffix))
     (when (some? children)
       ($ Stack {:sx #js {:height "36px"
                          :width "42px"}
                 :alignItems "center"
                 :direction "row-reverse"}
          children))
     (when source-handle-id
       ($ source-handle {:id source-handle-id :parent-id parent-id}))))

(defn- open-rotation-path-ocp [outcome-node-id]
  (rf/dispatch [:ocp/open {:id ::variants-rotation-ocp/ocp
                           :props {:outcome-node-id outcome-node-id}}]))

(defui ^:memo outcome-node [{:keys [id]}]
  (let [{:keys [label config ad-variants parent-id] :as node-data} (use-subscribe [::sub/node-data id])
        parent-data (use-subscribe [::sub/node-data parent-id])
        rotation-path? (rotation/variants-rotation? parent-data)
        path-count (count (:outcome-node-ids parent-data))
        path-weight (when rotation-path?
                      (rotation/path-display-weight parent-data config path-count))
        {:keys [id on-select on-delete on-duplicate on-duplicate-subtree]}
        (hook/use-node-outcome-control node-data)

        selected (use-subscribe [::sub/outcome-selected? id])
        highlighted (use-subscribe [::sub/outcome-highlighted? id])
        read-only (use-subscribe [::sub/editor-read-only])
        {:keys [node-type->explainer]} (uix/use-context context/decision-trees-context)
        has-errors (validation/use-node-errors node-type->explainer node-data selected)
        poi-outcome? (= (:decision-node-type node-data) :decision-tree-node/poi)
        parent-node (use-subscribe [::sub/node-data (when poi-outcome? parent-id)])
        has-conflicts? (uix/use-memo
                        #(when poi-outcome?
                           (poi-validation/poi-outcome-has-conflicts? node-data parent-node))
                        [poi-outcome? node-data parent-node])
        on-click (when-not read-only
                   (fn []
                     (if rotation-path?
                       (open-rotation-path-ocp id)
                       (on-select))))]
    ($ outcome-list-item {:on-click on-click
                          :selected selected
                          :highlighted highlighted
                          :has-errors (or has-errors has-conflicts?)
                          :selectable true
                          :label label
                          :suffix (when (some? path-weight) (str path-weight))
                          :variants ad-variants
                          :source-handle-id (when-not (rotation/weighted-node? parent-data) id)
                          :parent-id parent-id}
       (when-not read-only
         ($ details-popover
            {:id id
             :items (cond-> []
                      on-duplicate (conj {:label "Duplicate Outcome"
                                          :on-click on-duplicate})
                      on-duplicate-subtree (conj {:label "Duplicate Outcome + Path"
                                                  :on-click on-duplicate-subtree})
                      on-delete (conj {:label "Delete Outcome"
                                       :on-click on-delete}))})))))

(defui default-outcome-node
  [{:keys [on-enable on-disable on-select selected ad-variants active]}]
  (let [read-only (use-subscribe [::sub/editor-read-only])]
    ($ outcome-list-item {:on-click (when active on-select)
                          :selected selected
                          :selectable active
                          :label "Default"
                          :variants ad-variants}
       (when-not read-only
         ($ Switch {:on-click (fn [e] (.stopPropagation e))
                    :checked active
                    :onChange (fn [e]
                                (if (-> e .-target .-checked)
                                  (on-enable)
                                  (on-disable)))})))))

(defhook use-update-node-internals-for-outcomes [{:keys [id visible-outcome-ids]}]
  (let [update-node-internals (useUpdateNodeInternals)]
    (uix/use-effect (fn [] (update-node-internals (str id)))
                    [visible-outcome-ids id update-node-internals])))

(def max-visible-outcomes 10)

(defn filter-outcome-ids [outcome-node-ids node-id->node-data search-text]
  (if (str/blank? search-text)
    outcome-node-ids
    (let [search-lower (str/lower-case search-text)]
      (filterv (fn [outcome-id]
                 (let [node-data (get node-id->node-data outcome-id)
                       label (str/lower-case (or (:label node-data) ""))]
                   (str/includes? label search-lower)))
               outcome-node-ids))))

(defn sort-by-global-search [outcome-node-ids node-id->node-data global-search-text]
  (if (str/blank? global-search-text)
    outcome-node-ids
    (let [search-lower (str/lower-case global-search-text)
          outcome-matches? (fn [outcome-id]
                             (let [node-data (get node-id->node-data outcome-id)
                                   label (str/lower-case (or (:label node-data) ""))]
                               (str/includes? label search-lower)))]
      (vec (sort-by (comp not outcome-matches?) outcome-node-ids)))))

(defhook use-outcome-visibility [{:keys [outcome-node-ids]}]
  (let [[search-text set-search-text] (uix/use-state "")
        [visible-count set-visible-count] (uix/use-state max-visible-outcomes)
        node-id->node-data (use-subscribe [::sub/node-id->node-data])
        global-search-text (use-subscribe [::sub/ad-variant-search-text])
        selected-outcome-ids (use-subscribe [::sub/selected-outcome-node-ids])

        outcome-node-ids (or outcome-node-ids [])

        filtered-outcome-ids (uix/use-memo
                              (fn []
                                (filter-outcome-ids outcome-node-ids node-id->node-data search-text))
                              [outcome-node-ids node-id->node-data search-text])

        ;; Sort by global search - matching outcomes go to the top
        sorted-outcome-ids (uix/use-memo
                            (fn []
                              (sort-by-global-search filtered-outcome-ids node-id->node-data global-search-text))
                            [filtered-outcome-ids node-id->node-data global-search-text])

        ;; When searching, show all sorted results
        ;; When not searching, show up to visible-count items
        visible-outcome-ids (uix/use-memo
                             (fn []
                               (if-not (str/blank? search-text)
                                 sorted-outcome-ids
                                 (subvec sorted-outcome-ids 0 (min visible-count (count sorted-outcome-ids)))))
                             [sorted-outcome-ids visible-count search-text])

        ;; Find selected outcomes that would be hidden
        hidden-selected-outcome-ids (uix/use-memo
                                     (fn []
                                       (let [visible-set (set visible-outcome-ids)]
                                         (filterv (fn [outcome-id]
                                                    (and (contains? selected-outcome-ids outcome-id)
                                                         (not (contains? visible-set outcome-id))))
                                                  outcome-node-ids)))
                                     [visible-outcome-ids outcome-node-ids selected-outcome-ids])

        remaining-count (- (count sorted-outcome-ids) (count visible-outcome-ids))
        has-more (pos? remaining-count)
        is-expanded (> visible-count max-visible-outcomes)

        load-more (uix/use-callback
                   (fn []
                     (set-visible-count (+ visible-count max-visible-outcomes)))
                   [visible-count])

        collapse (uix/use-callback
                  (fn []
                    (set-visible-count max-visible-outcomes))
                  [])

        node-id->children (use-subscribe [::sub/node-id->children])]

    ;; Reset visible count when search changes
    (uix/use-effect
     (fn []
       (set-visible-count max-visible-outcomes))
     [search-text])

    ;; Manage visibility of child paths for outcomes:
    ;; child paths should only be visible when their origin outcome is visible too.
    (uix/use-effect
     (fn []
       (let [all-visible-set (into (set visible-outcome-ids) hidden-selected-outcome-ids)]
         (doseq [outcome-id outcome-node-ids]
           (when-let [edge-id (:edge-id (get-outcome-descendants node-id->children {:id outcome-id}))]
             (rf/dispatch [::event/update-sub-path-visibility
                           {:edge-id edge-id
                            :is-visible (contains? all-visible-set outcome-id)}])))))
     [visible-outcome-ids hidden-selected-outcome-ids outcome-node-ids node-id->children])

    {:search-text search-text
     :set-search-text set-search-text
     :visible-outcome-ids visible-outcome-ids
     :hidden-selected-outcome-ids hidden-selected-outcome-ids
     :has-more has-more
     :is-expanded is-expanded
     :remaining-count remaining-count
     :load-more load-more
     :collapse collapse}))

(defui outcome-section [{:keys [controls outcome-node-ids id] :as decision-node}]
  (let [default-outcome-node-control (hook/use-default-outcome-node-control decision-node)
        {:keys [search-text set-search-text visible-outcome-ids hidden-selected-outcome-ids
                has-more is-expanded remaining-count load-more collapse]}
        (use-outcome-visibility {:outcome-node-ids outcome-node-ids})
        show-search? (> (count outcome-node-ids) max-visible-outcomes)
        all-visible-outcome-ids (into hidden-selected-outcome-ids visible-outcome-ids)
        rotation? (rotation/variants-rotation? decision-node)]
    (use-update-node-internals-for-outcomes {:id id :visible-outcome-ids all-visible-outcome-ids})
    ($ Stack
       {:spacing 1.5
        :direction "column"}
       ($ Stack
          {:direction "row-reverse"
           :alignItems "center"
           :gap 1}
          (for [[control-id on-click] controls
                :let [{:keys [label icon]} (get node-library/node-control-mapping control-id)
                      button-label (if (and (= control-id :on-add-outcome) rotation?)
                                     "+ New item"
                                     label)
                      hide-icon? (and (= control-id :on-add-outcome) rotation?)]
                :when (not (and (node-conversion/generated-node? decision-node)
                                (= :on-refresh control-id)))]
            ($ Button
               {:key control-id
                :startIcon (when-not hide-icon? ($ SvgIcon {:component icon}))
                :variant "text"
                :onClick (fn [^js e]
                           (.stopPropagation e)
                           (on-click))}
               button-label)))

       (when (or (seq outcome-node-ids) (some? default-outcome-node-control))
         ($ Divider))

       ;; Search field for nodes with many outcomes
       (when show-search?
         ($ TextField
            {:size "small"
             :placeholder "Search outcomes..."
             :value search-text
             :onChange (fn [^js e]
                         (set-search-text (.. e -target -value)))
             :onClick (fn [^js e] (.stopPropagation e))
             :InputProps #js {:startAdornment
                              ($ InputAdornment
                                 {:position "start"}
                                 ($ Icon "search"))}}))

       ($ List {:as Stack :sx #js {:p 0 :gap 1}}
          (for [outcome-id visible-outcome-ids]
            ($ outcome-node {:id outcome-id :key outcome-id}))
          ;; Show hidden selected outcomes
          (when (seq hidden-selected-outcome-ids)
            ($ :<>
               ($ Typography {:sx #js {:alignSelf "center"}} "⋯")
               (for [outcome-id hidden-selected-outcome-ids]
                 ($ outcome-node {:id outcome-id :key outcome-id}))
               ($ Typography {:sx #js {:alignSelf "center"}} "⋯")))
          (when (some? default-outcome-node-control)
            ($ default-outcome-node default-outcome-node-control)))

       (when (or is-expanded has-more)
         ($ Stack
            {:direction "row"
             :justifyContent "space-between"}
            ($ Button
               {:variant "text"
                :color "grey"
                :onClick collapse
                :sx #js {:visibility (if is-expanded "visible" "hidden")}}
               "Close")
            ($ Button
               {:variant "text"
                :onClick load-more
                :endIcon ($ Icon {:fontSize "small"} "expand_more")
                :sx #js {:visibility (if has-more "visible" "hidden")}}
               (str "Show more (" remaining-count ")")))))))

(defui ^:memo decision-tree-decision-node [{:keys [selected id]}]
  (let [id (parse-long id)
        read-only (use-subscribe [::sub/editor-read-only])
        node-data (use-subscribe [::sub/node-data id])
        {:keys [on-duplicate on-delete] :as node-controls} (hook/use-node-controls node-data)
        {:keys [node-type->explainer]} (uix/use-context context/decision-trees-context)
        has-errors (validation/use-node-errors node-type->explainer node-data selected)
        dashed (use-subscribe [::sub/is-decision-node-dashed id])]
    ($ decision-tree-node-container {:selected selected :dashed dashed}
       ($ Stack {:direction "column"}
          ($ node-header {:node-data node-data
                          :sx #js {:mb 1.5}}
             (when (and (not read-only) has-errors)
               ($ warning-icon))

             (when (or on-duplicate on-delete)
               ($ details-popover
                  {:items (cond-> []
                            on-duplicate (conj {:label (-> node-library/node-control-mapping
                                                           :on-duplicate
                                                           :label)
                                                :on-click on-duplicate})
                            on-delete (conj {:label (-> node-library/node-control-mapping
                                                        :on-delete
                                                        :label)
                                             :on-click on-delete}))})))
          ($ outcome-section
             (assoc node-data
                    :controls (dissoc node-controls :on-duplicate :on-delete))))
       ($ target-handle node-data))))

(defn decision-tree-decision-node* [^js props]
  ($ decision-tree-decision-node {:selected (.-selected props)
                                  :id (.-id props)}))
